import { useState, useEffect, useRef } from "react";
import { FLOWCHART_DIAGRAM_TESTCASES } from "./testcases/flowchart";
import { SEQUENCE_DIAGRAM_TESTCASES } from "./testcases/sequence.ts";
import { CLASS_DIAGRAM_TESTCASES } from "./testcases/class.ts";
import { ERD_DIAGRAM_TESTCASES } from "./testcases/er.ts";
import { STATE_DIAGRAM_TESTCASES } from "./testcases/state.ts";
import { MINDMAP_DIAGRAM_TESTCASES } from "./testcases/mindmap.ts";
import { UNSUPPORTED_DIAGRAM_TESTCASES } from "./testcases/unsupported.ts";

import SingleTestCase, { TestCase } from "./SingleTestCase.tsx";
import type { ActiveTestCaseIndex, MermaidData } from "./index.tsx";
import { usePersistedSectionState } from "./usePersistedSectionState.ts";

interface TestcasesProps {
  onChange: (
    definition: MermaidData["definition"],
    activeTestCaseIndex: number | "custom" | null
  ) => void;
  onInsertMermaidSvg: (svgHtml: string, width: number, height: number) => void;
  activeTestCaseIndex: ActiveTestCaseIndex;
}

interface TestcaseSectionProps {
  name: string;
  testcases: (TestCase & { sectionName: string })[];
  startIndex: number;
  documentationHref: string;
  searchQuery: string;
  onChange: TestcasesProps["onChange"];
  onInsertMermaidSvg: TestcasesProps["onInsertMermaidSvg"];
}

const TestcaseSection = ({
  name,
  testcases,
  startIndex,
  documentationHref,
  searchQuery,
  onChange,
  onInsertMermaidSvg,
}: TestcaseSectionProps) => {
  const baseId = name.toLowerCase();
  const { isExpanded, handleToggle } = usePersistedSectionState(
    `testcases:${baseId}`,
    name === "Class" || searchQuery.length > 0
  );

  if (testcases.length === 0) {
    return null;
  }

  return (
    <details
      key={baseId}
      className="testcase-section"
      open={isExpanded}
      onToggle={handleToggle}
    >
      <summary>
        <span className="details-summary-text">
          {name} {"Diagram Examples"}
        </span>
        <span className="details-summary-actions">
          <a
            className="details-summary-link"
            href={documentationHref}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.open(documentationHref, "_blank", "noopener,noreferrer");
            }}
          >
            {"Docs"}
          </a>
          <span className="details-summary-meta">{testcases.length}</span>
        </span>
      </summary>
      {isExpanded ? (
        <div id={`${baseId}-container`} className="testcase-container">
          {testcases.map((testcase, index) => {
            const testcaseIndex = startIndex + index;

            return (
              <SingleTestCase
                key={`${testcase.type}-${index}`}
                index={testcaseIndex}
                onChange={(definition) => {
                  onChange(definition, testcaseIndex);
                }}
                onInsertMermaidSvg={onInsertMermaidSvg}
                testcase={testcase}
              />
            );
          })}
        </div>
      ) : null}
    </details>
  );
};

const Testcases = ({ onChange, onInsertMermaidSvg }: TestcasesProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const testcaseTypes = [
    {
      name: "Flowchart",
      testcases: FLOWCHART_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/syntax/flowchart.html",
    },
    {
      name: "Sequence",
      testcases: SEQUENCE_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/syntax/sequenceDiagram.html",
    },
    {
      name: "Class",
      testcases: CLASS_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/syntax/classDiagram.html",
    },
    {
      name: "ERD",
      testcases: ERD_DIAGRAM_TESTCASES,
      documentationHref:
        "https://mermaid.js.org/syntax/entityRelationshipDiagram.html",
    },
    {
      name: "State",
      testcases: STATE_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/syntax/stateDiagram.html",
    },
    {
      name: "Mindmap",
      testcases: MINDMAP_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/syntax/mindmap.html",
    },
    {
      name: "Unsupported",
      testcases: UNSUPPORTED_DIAGRAM_TESTCASES,
      documentationHref: "https://mermaid.js.org/intro/syntax-reference.html",
    },
  ].map((section, index, sections) => ({
    ...section,
    testcases: (section.testcases as any[]).map(tc => ({ ...tc, sectionName: section.name })),
    startIndex: sections
      .slice(0, index)
      .reduce((total, current) => total + current.testcases.length, 0),
  }));

  const filteredSections = testcaseTypes.map(section => {
    const filtered = section.testcases.filter(tc => 
      tc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tc.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tc.definition.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return { ...section, testcases: filtered };
  });

  return (
    <div className="testcases-explorer">
      <div className="search-container">
        <span className="search-icon">🔍</span>
        <input
          ref={searchInputRef}
          type="text"
          className="search-input"
          placeholder="Search examples... (Ctrl+K)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="search-shortcut-hint">
          {window.navigator.platform.includes("Mac") ? "⌘K" : "Ctrl+K"}
        </div>
        {searchQuery && (
          <button className="clear-search" onClick={() => setSearchQuery("")}>
            {"×"}
          </button>
        )}
      </div>
      <div className="sections-container">
        {filteredSections.map(
          ({ name, testcases, documentationHref, startIndex }) => {
            return (
              <TestcaseSection
                key={name}
                documentationHref={documentationHref}
                name={name}
                onChange={onChange}
                onInsertMermaidSvg={onInsertMermaidSvg}
                startIndex={startIndex}
                testcases={testcases}
                searchQuery={searchQuery}
              />
            );
          }
        )}
      </div>
    </div>
  );
};

export default Testcases;
